import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash2, AlertTriangle, Download, Search } from 'lucide-react';
import {
  partsApi,
  type Part,
  type PartImportResolved,
  type PartImportPreviewRow,
  type PartImportCommitRow,
} from '@/lib/api';
import { PreviewImportDialog } from '@/components/PreviewImportDialog';
import { StagedAttachments } from '@/components/StagedAttachments';
import { uploadStagedAttachments } from '@/lib/attachments';
import { exportToExcel, exportToCsv, downloadPartsTemplate } from '@/lib/excel';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, Thead, Tbody, Th, Td, Tr, SortableTh, type SortState } from '@/components/ui/table';
import { Dialog } from '@/components/ui/dialog';
import { PageHeader } from '@/components/ui/page-header';

const BLANK: Partial<Part> = {
  part_number: '', name: '', description: '', unit_cost: undefined,
  stock_quantity: 0, reorder_threshold: undefined, location: '', supplier: '',
};

export function PartsPage() {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Part | null>(null);
  const [form, setForm] = useState<Partial<Part>>(BLANK);
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortState>({ by: 'part_number', dir: 'asc' });

  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const onSort = (next: SortState) => { setSort(next); setPage(1); };

  const { data, isLoading } = useQuery({
    queryKey: ['parts', page, search, sort.by, sort.dir],
    queryFn: () => partsApi.list(page, 50, search || undefined, sort.by, sort.dir),
  });

  const save = useMutation({
    mutationFn: async (): Promise<{ failed: string[] }> => {
      if (editing) { await partsApi.update(editing.id, form); return { failed: [] }; }
      const created = await partsApi.create(form);
      const { failed } = await uploadStagedAttachments('part', created.id, stagedFiles);
      return { failed };
    },
    onSuccess: ({ failed }) => {
      qc.invalidateQueries({ queryKey: ['parts'] });
      closeDialog();
      if (failed.length) {
        alert(`${failed.length} file${failed.length > 1 ? 's' : ''} couldn't be uploaded: ${failed.join(', ')}. You can add ${failed.length > 1 ? 'them' : 'it'} from the part record.`);
      }
    },
  });

  const del = useMutation({
    mutationFn: (id: string) => partsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['parts'] }),
  });

  const openCreate = () => { setEditing(null); setForm(BLANK); setStagedFiles([]); setOpen(true); };
  const openEdit = (p: Part) => { setEditing(p); setForm(p); setStagedFiles([]); setOpen(true); };
  const closeDialog = () => { setOpen(false); setEditing(null); setForm(BLANK); setStagedFiles([]); };
  const set = (k: keyof Part, v: unknown) => setForm(f => ({ ...f, [k]: v }));

  const isLow = (p: Part) =>
    p.reorder_threshold !== null && p.reorder_threshold !== undefined &&
    p.stock_quantity <= p.reorder_threshold;

  const totalCount = data?.pagination.total ?? 0;
  const lowStockCount = data?.data.filter(isLow).length ?? 0;
  const tagText = totalCount === 0
    ? undefined
    : lowStockCount > 0
      ? `${totalCount} SKU${totalCount === 1 ? '' : 's'} · ${lowStockCount} low`
      : `${totalCount} SKU${totalCount === 1 ? '' : 's'}`;

  return (
    <div className="flex flex-col gap-4">
      <PageHeader
        title="Spare Parts"
        tag={tagText}
        actions={
          isAdmin ? <Button size="sm" onClick={openCreate}><Plus size={14} /> Add Part</Button> : undefined
        }
      />

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            type="text"
            placeholder="Search…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            className="pl-8 pr-3 py-1.5 text-sm border border-canvas-line bg-white rounded-lg w-56 focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        {(['Excel', 'CSV'] as const).map(fmt => (
                <Button
                  key={fmt}
                  variant="outline" size="sm"
                  onClick={async () => {
                    const all = await partsApi.list(1, 5000);
                    const rows = all.data.map(p => ({
                      part_number:       p.part_number,
                      name:              p.name,
                      description:       p.description ?? '',
                      location:          p.location ?? '',
                      supplier:          p.supplier ?? '',
                      stock_quantity:    String(p.stock_quantity),
                      reorder_threshold: p.reorder_threshold != null ? String(p.reorder_threshold) : '',
                      unit_cost:         p.unit_cost != null ? String(p.unit_cost) : '',
                    }));
                    fmt === 'Excel' ? exportToExcel(rows, 'parts_export') : exportToCsv(rows, 'parts_export');
                  }}
                >
                  <Download size={14} /> {fmt}
                </Button>
              ))}
              {isAdmin && (
                <PreviewImportDialog<PartImportResolved>
                  buttonLabel="Import"
                  dialogTitle="Import Parts"
                  noun="part"
                  nounPlural="parts"
                  onDownloadTemplate={downloadPartsTemplate}
                  fields={[
                    { key: 'part_number',       label: 'Part Number',      required: true,  description: 'Unique ID e.g. BRG-6205' },
                    { key: 'name',              label: 'Name',             required: true,  description: 'Part display name' },
                    { key: 'stock_quantity',    label: 'Stock Qty',        required: true,  description: 'Current quantity on hand' },
                    { key: 'description',       label: 'Description',      description: 'Optional description' },
                    { key: 'location',          label: 'Location',         description: 'Shelf / bin location' },
                    { key: 'supplier',          label: 'Supplier',         description: 'Preferred supplier name' },
                    { key: 'reorder_threshold', label: 'Reorder Threshold', description: 'Alert fires when stock ≤ this' },
                    { key: 'unit_cost',         label: 'Unit Cost ($)',    description: 'Cost per unit in dollars' },
                  ]}
                  previewColumns={[
                    { header: 'Part Number', render: r => r.resolved?.part_number ?? '—' },
                    { header: 'Name',        render: r => r.resolved?.name ?? '—' },
                    { header: 'Stock',       render: r => r.resolved ? String(r.resolved.stock_quantity) : '—' },
                    { header: 'Reorder',     render: r => r.resolved ? String(r.resolved.reorder_threshold) : '—' },
                    { header: 'Unit Cost',   render: r => r.resolved?.unit_cost != null ? `$${r.resolved.unit_cost}` : <span className="text-slate-400">—</span> },
                  ]}
                  importPreview={rows => partsApi.importPreview(rows as unknown as PartImportPreviewRow[])}
                  importCommit={rows => partsApi.importCommit(rows as unknown as PartImportCommitRow[])}
                  onComplete={() => qc.invalidateQueries({ queryKey: ['parts'] })}
                />
              )}
      </div>

      {isLoading ? (
        <p className="py-8 text-center text-slate-400">Loading…</p>
      ) : (
        <>
              {/* Mobile + tablet: card grid */}
              <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-2">
                {data?.data.length === 0 && (
                  <p className="text-center text-slate-400 text-sm py-6 col-span-full">No parts found.</p>
                )}
                {data?.data.map(p => (
                  <div
                    key={p.id}
                    onClick={() => navigate(`/parts/${p.id}`)}
                    className="bg-white rounded-lg border border-slate-200 p-3 cursor-pointer transition-colors active:bg-slate-50"
                    style={isLow(p) ? { borderLeftWidth: '3px', borderLeftColor: '#f59e0b' } : undefined}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-slate-900 text-sm leading-snug truncate">{p.name}</p>
                        <p className="text-xs text-slate-500 mt-0.5 font-mono">{p.part_number}</p>
                      </div>
                      {isLow(p) && (
                        <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-yellow-100 text-yellow-800 flex-shrink-0">
                          Low Stock
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-1">
                      Stock: <span className={isLow(p) ? 'text-red-600 font-medium' : 'font-medium text-slate-700'}>{p.stock_quantity}</span>
                      {p.reorder_threshold != null && <> (min: {p.reorder_threshold})</>}
                      {p.location && <> · {p.location}</>}
                    </p>
                    {isAdmin && (
                      <div
                        className="flex items-center gap-1 mt-3 pt-3 border-t border-slate-100"
                        onClick={e => e.stopPropagation()}
                      >
                        <button
                          onClick={() => openEdit(p)}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-slate-100 text-slate-600"
                        >
                          <Pencil size={12} /> Edit
                        </button>
                        <button
                          onClick={() => { if (confirm(`Delete ${p.name}?`)) del.mutate(p.id); }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 size={12} /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop: table */}
              <div className="hidden lg:block">
            <Table variant="cards">
              <Thead>
                <tr>
                  <SortableTh column="part_number"       sort={sort} onSort={onSort}>Part #</SortableTh>
                  <SortableTh column="name"              sort={sort} onSort={onSort}>Name</SortableTh>
                  <SortableTh column="location"          sort={sort} onSort={onSort}>Location</SortableTh>
                  <SortableTh column="stock_quantity"    sort={sort} onSort={onSort}>Stock</SortableTh>
                  <SortableTh column="reorder_threshold" sort={sort} onSort={onSort}>Reorder At</SortableTh>
                  <SortableTh column="unit_cost"         sort={sort} onSort={onSort}>Unit Cost</SortableTh>
                  <SortableTh column="supplier"          sort={sort} onSort={onSort}>Supplier</SortableTh>
                  {isAdmin && <Th className="w-20">Actions</Th>}
                </tr>
              </Thead>
              <Tbody>
                {data?.data.map(p => (
                  <Tr
                    key={p.id}
                    onClick={e => {
                      // Don't navigate when clicking action buttons
                      if ((e.target as HTMLElement).closest('button')) return;
                      navigate(`/parts/${p.id}`);
                    }}
                  >
                    <Td className="font-mono text-xs">{p.part_number}</Td>
                    <Td className="font-medium text-brand-600">{p.name}</Td>
                    <Td>{p.location ?? '—'}</Td>
                    <Td>
                      <span className={isLow(p) ? 'flex items-center gap-1 text-red-600 font-medium' : ''}>
                        {isLow(p) && <AlertTriangle size={13} />}
                        {p.stock_quantity}
                      </span>
                    </Td>
                    <Td>{p.reorder_threshold ?? '—'}</Td>
                    <Td>{p.unit_cost != null ? `$${Number(p.unit_cost).toFixed(2)}` : '—'}</Td>
                    <Td>{p.supplier ?? '—'}</Td>
                    {isAdmin && (
                      <Td>
                        <div className="flex gap-1">
                          <button onClick={() => openEdit(p)} className="p-1 rounded hover:bg-slate-100 text-slate-500"><Pencil size={14} /></button>
                          <button
                            onClick={() => { if (confirm(`Delete ${p.name}?`)) del.mutate(p.id); }}
                            className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
                {data?.data.length === 0 && (
                  <Tr><Td className="py-8 text-center text-slate-400" colSpan={8}>No parts found.</Td></Tr>
                )}
              </Tbody>
            </Table>
              </div>
            </>
          )}

      {(data?.pagination.total ?? 0) > 50 && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setPage(p => p - 1)} disabled={page === 1}>Prev</Button>
          <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page * 50 >= (data?.pagination.total ?? 0)}>Next</Button>
        </div>
      )}

      <Dialog open={open} onClose={closeDialog} title={editing ? 'Edit Part' : 'Add Part'}>
        <form onSubmit={e => { e.preventDefault(); save.mutate(); }} className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3">
            <Input label="Part Number *" value={form.part_number ?? ''} onChange={e => set('part_number', e.target.value)} required />
            <Input label="Name *" value={form.name ?? ''} onChange={e => set('name', e.target.value)} required />
            <Input label="Location" value={form.location ?? ''} onChange={e => set('location', e.target.value)} />
            <Input label="Supplier" value={form.supplier ?? ''} onChange={e => set('supplier', e.target.value)} />
            <Input label="Stock Qty *" type="number" min={0} value={form.stock_quantity ?? 0} onChange={e => set('stock_quantity', parseInt(e.target.value) || 0)} required />
            <Input label="Reorder Threshold" type="number" min={0} value={form.reorder_threshold ?? ''} onChange={e => set('reorder_threshold', e.target.value ? parseInt(e.target.value) : undefined)} />
            <Input label="Unit Cost ($)" type="number" step="0.01" min={0} value={form.unit_cost ?? ''} onChange={e => set('unit_cost', e.target.value ? parseFloat(e.target.value) : undefined)} />
            <Input label="Description" value={form.description ?? ''} onChange={e => set('description', e.target.value)} />
          </div>
          {!editing && (
            <div className="border-t border-slate-200 pt-3">
              <StagedAttachments files={stagedFiles} onChange={setStagedFiles} label="Attachments (optional)" />
            </div>
          )}
          {save.error && <p className="text-sm text-red-600">{(save.error as Error).message}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" onClick={closeDialog}>Cancel</Button>
            <Button type="submit" disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save'}</Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
